import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Box, Tabs, Tab, Typography, Paper, Button, Modal } from "@mui/material";
import NationalCouncil from "./NEC.jsx";
import StateCouncil from "./SEC.jsx";
import RegionalCouncil from "./REC.jsx";
import DistrictCounsil from "./DEC.jsx";
import ListIcon from "@mui/icons-material/List";
import AddIcon from "@mui/icons-material/Add"
import "./NetworkPage.css";
import { GroupsOutlined } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
// import ComingSoon from "../../../components/common/ComingSoon.jsx";
import { IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSelector } from "react-redux";
import { usePermission } from "../../../permissions/usePermission";
import ApiCallService from "../../../Service/ApiCallService";
import {
  canOpenNetworkMemberProfile,
  fetchIsNecCouncilMember,
  NEC_COUNCIL_NAME,
} from "../../../utils/necMembership";
import Spinner from "../../../components/common/Spinner.jsx";
import {
  NETWORK_ACTIVE_TAB_LEVEL_KEY,
  NETWORK_RESTORE_ONCE_KEY,
  NETWORK_FILTER_KEYS,
} from "./networkStateKeys";
const NetworkPage = () => {
  const [tab, setTab] = useState(0);
  const [allowScopedRestore] = useState(
    () => sessionStorage.getItem(NETWORK_RESTORE_ONCE_KEY) === "1",
  );
  const [show, setShow] = useState(false);
  const [isLandingPage, setIsLandingPage] = useState(false);
  const [members, setMembers] = useState([]);
  const [councilOptions, setCouncilOptions] = useState([]);
  const [committeeOptions, setCommitteeOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNecCouncilMember, setIsNecCouncilMember] = useState(false);
  const userDetails = useSelector((state) => state.user?.userDetails);

  useEffect(() => {
		window.scrollTo({top:0, behavior:"smooth"})
	},[])

  useEffect(() => {
    let active = true;

    const fetchMembership = async () => {
      const isNecMember = await fetchIsNecCouncilMember();
      if (active) {
        setIsNecCouncilMember(isNecMember);
      }
    };

    fetchMembership();

    return () => {
      active = false;
    };
  }, []);
  const permissionsState = useSelector((state) => state.permissions);

  const isProd = false
  const isMobile = useMediaQuery("(max-width:640px)");
  const { canByKey, hasPolicyRole, isSuperAdmin } = usePermission();
  const canAddNew = canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_ADD");
  const canViewOfficeBearerList = canByKey("COMMITTEE_MANAGEMENT_COMMITTEE_MEMBERS_LIST");
  const canOpenMemberProfile =
    canOpenNetworkMemberProfile({
      userDetails,
      isSuperAdmin,
      hasPolicyRole,
      canByKey,
    }) || isNecCouncilMember;
  const councilTabs = [
    {
      label: "National Executive Council",
      level: "NATIONAL",
    },
    {
      label: "State Executive Council",
      level: "STATE",
    },
    {
      label: "Regional Executive Council",
      level: "REGION",
    },
    {
      label: "District Executive Council",
      level: "DISTRICT",
    },
  ];

  const extractEntityId = (entity) => {
    if (!entity) return "";
    if (typeof entity === "string") return entity;
    return entity?._id || entity?.id || "";
  };

  const normalizeId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return String(value?._id || value?.id || value?.$oid || "");
    }
    return String(value);
  };

  const getMemberUserField = (member, field) =>
    member?.user?.[field] ||
    member?.userId?.[field] ||
    member?.userObjectId?.[field] ||
    member?.[field] ||
    "";

  const normalizeToken = (value) => String(value || "").trim().toLowerCase();

  const normalizeScopeValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return (
        value?._id ||
        value?.id ||
        value?.$oid ||
        value?.name ||
        value?.stateName ||
        value?.regionName ||
        value?.districtName ||
        value?.label ||
        ""
      );
    }
    return String(value);
  };

  const extractEntityName = (entity, fallbacks = []) => {
    if (!entity) return "";
    if (typeof entity === "string") return entity;
    for (const key of fallbacks) {
      if (entity?.[key]) return entity[key];
    }
    return "";
  };

  const scopes = Array.isArray(permissionsState?.permissions?.scopes)
    ? permissionsState.permissions.scopes
    : [];
  const committeeScopes = scopes.filter((scope) => scope?.sourceType === "COMMITTEE");

  const normalizeScope = (scope) => {
    const payload = scope?.scope || scope;
    return {
      level: payload?.level,
      state: normalizeScopeValue(payload?.state),
      region: normalizeScopeValue(payload?.region),
      district: normalizeScopeValue(payload?.district),
    };
  };

  const allowedStateIds = useMemo(() => {
    const ids = committeeScopes
      .map((scope) => normalizeScope(scope).state)
      .filter(Boolean);
    return [...new Set(ids.map((id) => normalizeToken(id)).filter(Boolean))];
  }, [committeeScopes]);

  const allowedRegionIds = useMemo(() => {
    const ids = committeeScopes
      .map((scope) => normalizeScope(scope).region)
      .filter(Boolean);
    return [...new Set(ids.map((id) => normalizeToken(id)).filter(Boolean))];
  }, [committeeScopes]);

  const allowedDistrictIds = useMemo(() => {
    const ids = committeeScopes
      .map((scope) => normalizeScope(scope).district)
      .filter(Boolean);
    return [...new Set(ids.map((id) => normalizeToken(id)).filter(Boolean))];
  }, [committeeScopes]);

  const hasCommitteeScope = committeeScopes.length > 0;
  const levelRank = {
    NATIONAL: 3,
    STATE: 2,
    REGION: 1,
    DISTRICT: 0,
  };
  const maxRank = committeeScopes.reduce((acc, scope) => {
    const level = scope?.scope?.level;
    const rank = levelRank[level] ?? -1;
    return rank > acc ? rank : acc;
  }, -1);
  const canSeeLevel = (level) => {
    const rank = levelRank[level] ?? -1;
    return rank <= maxRank;
  };
  const visibleCouncilTabs = useMemo(() => (
    !hasCommitteeScope
      ? councilTabs
      : councilTabs.filter((tab) => canSeeLevel(tab.level))
  ), [hasCommitteeScope, maxRank]);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  useEffect(() => {
    if (!visibleCouncilTabs.length) return;

    const routeLevel = location?.state?.networkTabLevel;
    const storedLevel = allowScopedRestore
      ? sessionStorage.getItem(NETWORK_ACTIVE_TAB_LEVEL_KEY)
      : "";
    const preferredLevel = routeLevel || storedLevel;
    if (!preferredLevel) return;

    const restoredTabIndex = visibleCouncilTabs.findIndex(
      (tabItem) => tabItem.level === preferredLevel,
    );
    if (restoredTabIndex >= 0) {
      setTab(restoredTabIndex);
    }
  }, [visibleCouncilTabs, location?.state, allowScopedRestore]);

  useEffect(() => {
    if (allowScopedRestore) {
      sessionStorage.removeItem(NETWORK_RESTORE_ONCE_KEY);
      return;
    }
    sessionStorage.removeItem(NETWORK_ACTIVE_TAB_LEVEL_KEY);
    NETWORK_FILTER_KEYS.forEach((key) => sessionStorage.removeItem(key));
  }, [allowScopedRestore]);

  useEffect(() => {
    const activeLevel = visibleCouncilTabs[tab]?.level;
    if (!activeLevel) return;
    sessionStorage.setItem(NETWORK_ACTIVE_TAB_LEVEL_KEY, activeLevel);
  }, [tab, visibleCouncilTabs]);

  useEffect(() => {
    const fetchCouncilsDetails = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/committeeDetails/councils`
        );
        setCouncilOptions(res.data.data);
      } catch (err) {
        console.error("Error fetching members:", err);
      }
    };

    fetchCouncilsDetails();
  }, []);


  useEffect(() => {
    if (!councilOptions.length) return;

    const active = visibleCouncilTabs[tab];
    if (!active) return;

    const selectedCouncil = councilOptions.find(
      (c) => c.councilName === active.label
    );

    if (!selectedCouncil?._id) return;

    const fetchFiltered = async () => {
      try {
        setLoading(true)
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/committeeMembers/membersByCouncil`,
          { params: { councilId: selectedCouncil._id } }
        );
        let rawMembers = Array.isArray(res?.data?.data) ? res.data.data : [];

        if (!rawMembers.length && selectedCouncil?._id) {
          const fallbackRes = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/committeeMembers/membersByCouncil`,
            { params: { councilId: selectedCouncil._id, councilName: selectedCouncil.councilName } },
          );
          rawMembers = Array.isArray(fallbackRes?.data?.data)
            ? fallbackRes.data.data
            : [];
        }
        if (!rawMembers.length) {
          console.warn("No members returned for council", {
            councilId: selectedCouncil?._id,
            councilName: selectedCouncil?.councilName,
          });
        }

        const formatted = rawMembers.map((m) => ({
          _id: m._id || m.user?._id,
          userId: m.user?._id || "",
          salutation:
            getMemberUserField(m, "salutation") ||
            getMemberUserField(m, "Salutation") ||
            "",
          userFirstName: getMemberUserField(m, "userFirstName"),
          userMiddleName: getMemberUserField(m, "userMiddleName"),
          userLastName: getMemberUserField(m, "userLastName"),
          userImageURL: m.user?.userImageURL || "",
          committee: m.committee?.name || "",
          committeeType: m.committeeType?.name || "",
          committeeSubtype:
            m.committee?.committeeSubtype ||
            m.committee?.committeeSubType ||
            m.committeeType?.committeeSubtype ||
            m.committeeType?.committeeSubType ||
            "",
          committeeSubType:
            m.committee?.committeeSubType ||
            m.committee?.committeeSubtype ||
            m.committeeType?.committeeSubType ||
            m.committeeType?.committeeSubtype ||
            "",
          position: m.designations?.map((d) => d.title).join(", ") || "",
          city: m.user?.city || "",
          stateId:
            normalizeId(extractEntityId(m.state)) ||
            normalizeId(extractEntityId(m.user?.state)) ||
            normalizeId(m.user?.stateId) ||
            "",
          districtId:
            normalizeId(extractEntityId(m.district)) ||
            normalizeId(extractEntityId(m.user?.district)) ||
            normalizeId(m.user?.districtId) ||
            "",
          regionId:
            normalizeId(extractEntityId(m.region)) ||
            normalizeId(extractEntityId(m.user?.region)) ||
            normalizeId(m.user?.regionId) ||
            "",
          state:
            extractEntityName(m.state, ["stateName", "name", "label"]) ||
            m.stateName ||
            m.state_name ||
            extractEntityName(m.location?.state, ["stateName", "name", "label"]) ||
            extractEntityName(m.user?.state, ["stateName", "name", "label"]) ||
            m.user?.stateName ||
            m.user?.state ||
            "",
          region:
            extractEntityName(m.region, ["regionName", "name", "label"]) ||
            m.regionName ||
            m.region_name ||
            extractEntityName(m.location?.region, ["regionName", "name", "label"]) ||
            extractEntityName(m.user?.region, ["regionName", "name", "label"]) ||
            m.user?.regionName ||
            m.user?.region ||
            "",
          district:
            extractEntityName(m.district, ["districtName", "name", "label"]) ||
            m.districtName ||
            m.district_name ||
            extractEntityName(m.location?.district, ["districtName", "name", "label"]) ||
            extractEntityName(m.user?.district, ["districtName", "name", "label"]) ||
            m.user?.districtName ||
            m.user?.district ||
            "",

          council: m.council?.name || "",
          joiningDate: m.joinDate || "",
          updatedAt: m.updatedAt || m.user?.updatedAt || "",
          userMobile: m.user?.userMobile || m.userMobile || m.user?.mobileNumber || "",
          userEmail: m.user?.userEmail || m.userEmail || m.user?.email || "",
        }));
        const dedupedMembers = Array.from(
          new Map(formatted.map((member) => [member._id, member])).values()
        );
        setMembers(dedupedMembers);
      } catch (err) {
        setLoading(false)
        console.error("Error fetching filtered members:", err);
      }
      setLoading(false)
    };
    fetchFiltered();
  }, [tab, councilOptions, visibleCouncilTabs]);

  useEffect(() => {
    if (tab >= visibleCouncilTabs.length) {
      setTab(0);
    }
  }, [tab, visibleCouncilTabs.length]);

  const scopedMembers = useMemo(() => {
    if (!hasCommitteeScope) return members;
    const active = visibleCouncilTabs[tab];
    if (!active) return members;

    const hasAnyTokenMatch = (allowedTokens = [], values = []) => {
      if (!allowedTokens.length) return false;
      const valueTokens = values.map((v) => normalizeToken(v)).filter(Boolean);
      return valueTokens.some((token) => allowedTokens.includes(token));
    };

    switch (active.level) {
      case "STATE":
        return allowedStateIds.length
          ? members.filter((m) =>
            hasAnyTokenMatch(allowedStateIds, [m.stateId, m.state]),
          )
          : members;
      case "REGION":
        if (allowedRegionIds.length) {
          const regionMatched = members.filter((m) =>
            hasAnyTokenMatch(allowedRegionIds, [m.regionId, m.region]),
          );
          if (regionMatched.length) return regionMatched;
        }
        if (allowedStateIds.length) {
          return members.filter((m) =>
            hasAnyTokenMatch(allowedStateIds, [m.stateId, m.state]),
          );
        }
        return members;
      case "DISTRICT":
        if (allowedDistrictIds.length) {
          const districtMatched = members.filter((m) =>
            hasAnyTokenMatch(allowedDistrictIds, [m.districtId, m.district]),
          );
          if (districtMatched.length) return districtMatched;
        }
        if (allowedStateIds.length) {
          return members.filter((m) =>
            hasAnyTokenMatch(allowedStateIds, [m.stateId, m.state]),
          );
        }
        return members;
      default:
        return members;
    }
  }, [
    members,
    hasCommitteeScope,
    visibleCouncilTabs,
    tab,
    allowedStateIds,
    allowedRegionIds,
    allowedDistrictIds,
  ]);

  const renderTabContent = () => {
    const active = visibleCouncilTabs[tab];
    if (!active) return null;

    const tabComponents = {
      NATIONAL: NationalCouncil,
      STATE: StateCouncil,
      REGION: RegionalCouncil,
      DISTRICT: DistrictCounsil,
    };

    const ActiveComponent = tabComponents[active.level];
    if (!ActiveComponent) return null;

    return (
      <ActiveComponent
        members={active.level === "NATIONAL" ? members : scopedMembers}
        allowScopedRestore={allowScopedRestore}
        canOpenMemberProfile={canOpenMemberProfile}
      />
    );
  }

  const maxTabs = visibleCouncilTabs.length || 0;

  const nextTab = () =>
    setTab((prev) => Math.min(prev + 1, Math.max(maxTabs - 1, 0)));

  const prevTab = () =>
    setTab((prev) => Math.max(prev - 1, 0));



  return (
    <div style={{ backgroundColor: "#fff" }}>


      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          p: 2,
        }}
      >
        {/* Center Title */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            textAlign: "left",
            flex: 1,
            minWidth: 0,
            lineHeight: 1.1,
            overflow: "hidden",
            // textOverflow: "ellipsis",
            whiteSpace: "wrap",
          }}
        >
          Office-Bearer Network
        </Typography>

        {/* Right Actions */}
        <Box
          sx={{
            position: "absolute",
            right: 16,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {canAddNew && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={
                isProd
                  ? () => setOpen(true)
                  : () =>
                    navigate("/committee/registerNewUser", {
                      state: { mode: "create" },
                    })
              }
              sx={{
                minWidth: isMobile ? 40 : "auto",
                px: isMobile ? 1 : 2,
              }}
            >
              <AddIcon fontSize="small" />
              {!isMobile && "Add New"}
            </Button>
          )}



          {/* {canViewOfficeBearerList && (
            <Button
              variant="outlined"
              onClick={() => navigate("/officeBearerList")}
              sx={{
                minWidth: 0,
                padding: "2px",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.secondary.figma
                },
              }}
            >
              <ListIcon sx={{ fontSize: 24 }} />
            </Button>
          )} */}

        </Box>
      </Box>



      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* LEFT ARROW (mobile only) */}
          {isMobile && (
            <IconButton onClick={prevTab} size="small">
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* TABS */}
          <Tabs
            value={tab}
            onChange={(e, newValue) => setTab(newValue)}
            variant="scrollable"
            scrollButtons={false}
            indicatorColor="primary"
            textColor="primary"
            sx={{
              flex: 1,
              backgroundColor: "#fff",
              borderBottom: "1px solid #e0e0e0",

            }}
          >
            {visibleCouncilTabs.map((tabItem) => (
              <Tab key={tabItem.label} label={tabItem.label} sx={{ ml: 2 }} />
            ))}
          </Tabs>

          {/* RIGHT ARROW (mobile only) */}
          {isMobile && (
            <IconButton onClick={nextTab} size="small">
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box sx={{ position: "relative", minHeight: 200 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 200,
            }}
          >
            <Spinner loading={loading} />
          </Box>
        ) : (
          renderTabContent()
        )}
      </Box>

    </div>
  );
};

export default NetworkPage;










